import React, { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';

import { groupBy } from 'lodash';
import { useSnackbar } from 'notistack';

import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Container,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { HERO_PLANNER_DEBUG_PATH, HERO_PLANNER_DEBUG_SEARCH_PARAMS } from '~/consts/customerCommunication/heroPlanner';

import {
  useGetHeroPlannerDebugCountryGroups,
  useGetHeroPlannerDebugHeroRows,
  useGetHeroPlannerDebugPreSendChecks,
  useGetHeroPlannerDebugSchedules,
  useGetHeroPlannerDebugTargets,
  useRecheckHeroPlannerDebugSchedule,
  useRerunHeroPlannerDebugSchedules,
} from '~/hooks/apis/customer-communication/useHeroPlannerScheduleDebug';
import { useAppSelector } from '~/hooks/store';
import useAppNavigation from '~/hooks/useAppNavigation';
import useQuery from '~/hooks/useQuery';

import { Dayjs, getDayJs } from '~/services/TimeService';

import { EmptyArray } from '~/utils/arrayUtils';

import HeroPlannerDebugCountryGroupSection from '../Components/Debug/HeroPlannerDebugCountryGroupSection';
import HeroPlannerDebugResponseDialog from '../Components/Debug/HeroPlannerDebugResponseDialog';
import HeroPlannerDebugSummary from '../Components/Debug/HeroPlannerDebugSummary';
import {
  HeroPlannerDebugRow,
  buildHeroPlannerDebugRow,
  getHeroPlannerDebugExperienceCities,
  getHeroPlannerDebugSkipReason,
  isHeroPlannerDebugExperienceTarget,
  isHeroPlannerDebugPreSendCheckPassing,
  isHeroPlannerDebugProblemRow,
  sortHeroPlannerDebugRows,
  summariseHeroPlannerDebugRows,
} from '../utils/heroPlannerDebugUtils';
import { getHeroPlannerGenerationSegmentName } from '../utils/heroPlannerGenerationUtils';

const SEND_DATE_FORMAT = 'YYYY-MM-DD';
// sweeps every country group at once; opt-in only, since it's several times the requests of a single group
const ALL_COUNTRY_GROUPS = 'all';

enum RESULT_FILTERS {
  ALL = 'all',
  FAILURES = 'failures',
  OFFER = 'offer',
}

function isResultFilter(value: string | null): value is RESULT_FILTERS {
  return Object.values(RESULT_FILTERS).includes(value as RESULT_FILTERS);
}

function getToday() {
  return getDayJs().tz(window.configs.DEFAULT_TIMEZONE);
}

function HeroPlannerDebugPage() {
  const brand = useAppSelector((state) => state.tenant.brand);
  const searchParams = useQuery();
  const { replace } = useAppNavigation();
  const { enqueueSnackbar } = useSnackbar();

  const [sendDate, setSendDate] = useState<Dayjs>(() => {
    const fromUrl = searchParams.get(HERO_PLANNER_DEBUG_SEARCH_PARAMS.SEND_DATE);
    const parsed = fromUrl ? getDayJs(fromUrl) : undefined;
    // fall back to today, the date most debugging happens for
    return parsed?.isValid() ? parsed : getToday();
  });
  const [offerId, setOfferId] = useState(() => searchParams.get(HERO_PLANNER_DEBUG_SEARCH_PARAMS.OFFER_ID) ?? '');
  const [resultFilter, setResultFilter] = useState<RESULT_FILTERS>(() => {
    const fromUrl = searchParams.get(HERO_PLANNER_DEBUG_SEARCH_PARAMS.SHOW);
    return isResultFilter(fromUrl) ? fromUrl : RESULT_FILTERS.ALL;
  });
  // one country group at a time keeps each sweep small; defaults to the first group once they've loaded, never to all
  const [countryGroupId, setCountryGroupId] = useState<string | undefined>(
    () => searchParams.get(HERO_PLANNER_DEBUG_SEARCH_PARAMS.COUNTRY_GROUP) ?? undefined,
  );
  const [showUnsent, setShowUnsent] = useState(
    () => searchParams.get(HERO_PLANNER_DEBUG_SEARCH_PARAMS.SHOW_UNSENT) === 'true',
  );
  const [inspectedRow, setInspectedRow] = useState<HeroPlannerDebugRow>();

  const sendDateValue = sendDate.format(SEND_DATE_FORMAT);
  const trimmedOfferId = offerId.trim();
  // matching an offer walks every schedule, so let typing stay responsive while the rows catch up
  const deferredOfferId = useDeferredValue(trimmedOfferId);
  // "has offer" means nothing without an offer to look for
  const activeResultFilter =
    resultFilter === RESULT_FILTERS.OFFER && !trimmedOfferId ? RESULT_FILTERS.ALL : resultFilter;

  // QUERIES
  const countryGroupsQuery = useGetHeroPlannerDebugCountryGroups(brand);
  const countryGroups = countryGroupsQuery.data ?? EmptyArray;
  const isAllCountryGroups = countryGroupId === ALL_COUNTRY_GROUPS;
  const selectedCountryGroup = isAllCountryGroups
    ? undefined
    : (countryGroups.find((countryGroup) => countryGroup.id === countryGroupId) ?? countryGroups[0]);
  const selectedCountryGroupId = isAllCountryGroups ? ALL_COUNTRY_GROUPS : selectedCountryGroup?.id;
  const selectedCountryGroups = useMemo(
    () => (isAllCountryGroups ? countryGroups : selectedCountryGroup ? [selectedCountryGroup] : EmptyArray),
    [countryGroups, isAllCountryGroups, selectedCountryGroup],
  );
  const selectedCountryGroupIds = useMemo(
    () => selectedCountryGroups.map((countryGroup) => countryGroup.id),
    [selectedCountryGroups],
  );
  const selectedCountryGroupsName = isAllCountryGroups ? 'every country group' : selectedCountryGroup?.name;

  // keep the URL shareable, so "here's where it's failing" can be sent as a link
  useEffect(() => {
    const params = new URLSearchParams();
    params.set(HERO_PLANNER_DEBUG_SEARCH_PARAMS.SEND_DATE, sendDateValue);
    if (selectedCountryGroupId) {
      params.set(HERO_PLANNER_DEBUG_SEARCH_PARAMS.COUNTRY_GROUP, selectedCountryGroupId);
    }
    if (trimmedOfferId) {
      params.set(HERO_PLANNER_DEBUG_SEARCH_PARAMS.OFFER_ID, trimmedOfferId);
    }
    if (activeResultFilter !== RESULT_FILTERS.ALL) {
      params.set(HERO_PLANNER_DEBUG_SEARCH_PARAMS.SHOW, activeResultFilter);
    }
    if (showUnsent) {
      params.set(HERO_PLANNER_DEBUG_SEARCH_PARAMS.SHOW_UNSENT, 'true');
    }
    replace(`${HERO_PLANNER_DEBUG_PATH}?${params.toString()}`);
  }, [activeResultFilter, replace, selectedCountryGroupId, sendDateValue, showUnsent, trimmedOfferId]);

  const selectedTargets = useGetHeroPlannerDebugTargets(brand, selectedCountryGroups);
  const { targets } = selectedTargets;
  const failedDiscoveries = selectedTargets.countryGroups.filter(({ query }) => query.isError);
  const isDiscovered = !selectedTargets.isPending && failedDiscoveries.length < selectedTargets.countryGroups.length;
  const { heroRows, pendingCountryGroupIds, incompleteCountryGroupIds } = useGetHeroPlannerDebugHeroRows(
    brand,
    sendDateValue,
    selectedTargets.countryGroups,
  );
  const experienceCities = useMemo(() => getHeroPlannerDebugExperienceCities(heroRows), [heroRows]);
  const countryGroupNames = useMemo(
    () => new Map(countryGroups.map((countryGroup) => [countryGroup.id, countryGroup.name])),
    [countryGroups],
  );

  // segments that are never sent aren't requested at all; a failure there doesn't matter
  const skipReasons = useMemo(() => {
    const reasons = new Map<string, string>();
    targets.forEach((target) => {
      const reason = getHeroPlannerDebugSkipReason(target, brand, experienceCities);
      if (reason) {
        reasons.set(target.key, reason);
      }
    });
    return reasons;
  }, [brand, experienceCities, targets]);

  const checkedTargets = useMemo(
    () =>
      targets.filter(
        (target) =>
          !skipReasons.has(target.key) &&
          // hold experience segments back until we know whether their city is sent
          !(pendingCountryGroupIds.has(target.countryGroup) && isHeroPlannerDebugExperienceTarget(target, brand)),
      ),
    [brand, pendingCountryGroupIds, skipReasons, targets],
  );
  const scheduleOutcomes = useGetHeroPlannerDebugSchedules(brand, sendDateValue, checkedTargets);
  const preSendChecks = useGetHeroPlannerDebugPreSendChecks(brand, sendDateValue, selectedCountryGroupIds);
  const rerunSweep = useRerunHeroPlannerDebugSchedules(brand, sendDateValue, selectedCountryGroupIds);
  const handleRetry = useRecheckHeroPlannerDebugSchedule(brand, sendDateValue);

  const rows = useMemo(() => {
    const outcomesByKey = new Map(checkedTargets.map((target, index) => [target.key, scheduleOutcomes[index]]));

    return targets.map((target) =>
      buildHeroPlannerDebugRow(target, {
        brand,
        outcome: outcomesByKey.get(target.key),
        offerId: deferredOfferId,
        skipReason: skipReasons.get(target.key),
        heroRows: heroRows.get(target.countryGroup),
        isHeroRowsPending: pendingCountryGroupIds.has(target.countryGroup),
      }),
    );
  }, [
    brand,
    checkedTargets,
    deferredOfferId,
    heroRows,
    pendingCountryGroupIds,
    scheduleOutcomes,
    skipReasons,
    targets,
  ]);
  const summary = useMemo(() => summariseHeroPlannerDebugRows(rows), [rows]);

  const failingPreSendChecks = preSendChecks.filter(
    (check) => check.query.isSuccess && !isHeroPlannerDebugPreSendCheckPassing(check.query.data),
  );
  const isPreSendCheckSettled = preSendChecks.every((check) => !check.query.isPending && !check.query.isFetching);
  const isEverythingPassing =
    summary.loading === 0 &&
    summary.total > 0 &&
    summary.ok === summary.total &&
    isPreSendCheckSettled &&
    preSendChecks.every((check) => isHeroPlannerDebugPreSendCheckPassing(check.query.data));

  // CALLBACKS
  const handleCopyUrl = useCallback(
    async (url: string) => {
      try {
        await navigator.clipboard.writeText(url);
        enqueueSnackbar('Request URL copied', { variant: 'success' });
      } catch {
        enqueueSnackbar('Could not copy to the clipboard', { variant: 'error' });
      }
    },
    [enqueueSnackbar],
  );

  const handleSendDateChange = useCallback((value: Dayjs | null) => {
    if (value?.isValid()) {
      setSendDate(value);
    }
  }, []);

  const handleResultFilterChange = useCallback((_event: React.MouseEvent, value: RESULT_FILTERS | null) => {
    if (value) {
      setResultFilter(value);
    }
  }, []);

  const closeInspectedRow = useCallback(() => setInspectedRow(undefined), []);

  // one section per discovered country group, each with its own counts and pre-send check
  const sections = useMemo(() => {
    const rowsByCountryGroupId = groupBy(rows, (row) => row.target.countryGroup);
    return selectedTargets.countryGroups
      .filter(({ query }) => query.isSuccess)
      .map(({ countryGroup }) => {
        const groupRows = rowsByCountryGroupId[countryGroup.id] ?? EmptyArray;
        return {
          countryGroup,
          summary: summariseHeroPlannerDebugRows(groupRows),
          preSendChecks: preSendChecks.filter((check) => check.countryGroup === countryGroup.id),
          visibleRows: sortHeroPlannerDebugRows(
            groupRows.filter((row) => {
              if (row.status === 'skipped' && !showUnsent) {
                return false;
              }
              if (activeResultFilter === RESULT_FILTERS.FAILURES) {
                return isHeroPlannerDebugProblemRow(row);
              }
              if (activeResultFilter === RESULT_FILTERS.OFFER) {
                return row.offerSlots.length > 0;
              }
              return true;
            }),
          ),
        };
      });
  }, [activeResultFilter, preSendChecks, rows, selectedTargets.countryGroups, showUnsent]);

  const failingPreSendCheckDescription = isAllCountryGroups
    ? `${failingPreSendChecks
        .map(({ countryGroup, segment }) => {
          const name = countryGroupNames.get(countryGroup) ?? countryGroup;
          return segment ? `${name} (${getHeroPlannerGenerationSegmentName(segment)})` : name;
        })
        .join(', ')} ${failingPreSendChecks.length === 1 ? 'fails its' : 'fail their'} pre-send check`
    : failingPreSendChecks.some(({ segment }) => segment)
      ? `The ${failingPreSendChecks
          .map(({ segment }) => getHeroPlannerGenerationSegmentName(segment))
          .join(' and ')} pre-send check fails`
      : 'Its pre-send check fails';

  return (
    <Container maxWidth="xl">
      <PageHeader title="Hero Planner Debugger" backButton="/customer-communication/hero-planner/schedules">
        <Tooltip title="Checks the selected country groups' segments and pre-send checks again. Segments are only discovered once per visit, so reload the page after adding a country, state or city.">
          <span>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={rerunSweep}
              disabled={!targets.length || summary.loading > 0}
            >
              Re-run checks
            </Button>
          </span>
        </Tooltip>
      </PageHeader>

      <Stack gap={3} pb={4}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems={{ md: 'center' }}>
            <DatePicker
              label="Send date"
              value={sendDate}
              format="DD/MM/YYYY"
              onChange={handleSendDateChange}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
            />
            <TextField
              size="small"
              label="Trace an offer"
              placeholder="Offer ID"
              value={offerId}
              onChange={(event) => setOfferId(event.target.value)}
              sx={{ minWidth: 300, flex: { md: 1 }, maxWidth: { md: 420 } }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: offerId ? (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Clear offer ID" onClick={() => setOfferId('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                },
              }}
            />
            <ToggleButtonGroup
              exclusive
              size="small"
              value={activeResultFilter}
              onChange={handleResultFilterChange}
              aria-label="Filter results"
            >
              <ToggleButton value={RESULT_FILTERS.ALL}>All</ToggleButton>
              <ToggleButton value={RESULT_FILTERS.FAILURES}>
                Failures ({summary.failed + summary.regenerating})
              </ToggleButton>
              <ToggleButton value={RESULT_FILTERS.OFFER} disabled={!trimmedOfferId}>
                Has offer ({trimmedOfferId ? summary.withOffer : 0})
              </ToggleButton>
            </ToggleButtonGroup>
            <FormControlLabel
              control={<Switch checked={showUnsent} onChange={(event) => setShowUnsent(event.target.checked)} />}
              label={`Show never-sent segments (${summary.skipped})`}
            />
          </Stack>

          {countryGroups.length > 1 && (
            <Stack direction="row" gap={1} flexWrap="wrap" mt={2} role="radiogroup" aria-label="Country group">
              {/* describeChild keeps "All country groups" as the chip's name; by default the title would replace it */}
              <Tooltip
                describeChild
                title="Checks every country group at once, which takes several times as many requests as one group"
              >
                <Chip
                  role="radio"
                  aria-checked={isAllCountryGroups}
                  label="All country groups"
                  color={isAllCountryGroups ? 'primary' : 'default'}
                  variant={isAllCountryGroups ? 'filled' : 'outlined'}
                  onClick={() => setCountryGroupId(ALL_COUNTRY_GROUPS)}
                />
              </Tooltip>
              {countryGroups.map((countryGroup) => {
                const isSelected = countryGroup.id === selectedCountryGroupId;
                return (
                  <Chip
                    key={countryGroup.id}
                    role="radio"
                    aria-checked={isSelected}
                    label={countryGroup.name}
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                    onClick={() => setCountryGroupId(countryGroup.id)}
                  />
                );
              })}
            </Stack>
          )}
        </Paper>

        {countryGroupsQuery.isError && (
          <Alert
            severity="error"
            action={
              <Button size="small" color="inherit" onClick={() => countryGroupsQuery.refetch()}>
                Retry
              </Button>
            }
          >
            <AlertTitle>Couldn't load the Hero Planner country groups</AlertTitle>
            {String(countryGroupsQuery.error)}
          </Alert>
        )}

        {countryGroupsQuery.isSuccess && countryGroups.length === 0 && (
          <Alert severity="info">No Hero Planner country groups were found for this brand.</Alert>
        )}

        {failedDiscoveries.map(({ countryGroup, query }) => (
          <Alert
            key={countryGroup.id}
            severity="error"
            action={
              <Button size="small" color="inherit" onClick={() => query.refetch()}>
                Retry
              </Button>
            }
          >
            <AlertTitle>Couldn't load the Hero Planner segments to check for {countryGroup.name}</AlertTitle>
            {String(query.error)}
          </Alert>
        ))}

        {(countryGroupsQuery.isPending || selectedTargets.isPending) && (
          <Stack gap={2}>
            <Typography variant="body2" color="text.secondary">
              Finding every country, state, city and segment
              {selectedCountryGroupsName ? ` in ${selectedCountryGroupsName}` : ''}…
            </Typography>
            <Skeleton variant="rounded" height={120} />
            <Skeleton variant="rounded" height={240} />
          </Stack>
        )}

        {isDiscovered && (
          <>
            <HeroPlannerDebugSummary summary={summary} offerId={deferredOfferId} />
            <Typography variant="caption" color="text.secondary" mt={-2}>
              Each segment is checked with DEFAULT cadence, for every loyalty variant. The pre-send check covers every
              cadence too, plus hero planner edits made after tail deals were generated.
            </Typography>

            {failingPreSendChecks.length > 0 && (
              <Alert severity="error">
                <AlertTitle>
                  The send won't go out for {isAllCountryGroups ? 'some country groups' : selectedCountryGroup?.name}
                </AlertTitle>
                {failingPreSendCheckDescription} - the reasons are listed below.
              </Alert>
            )}

            {selectedTargets.discoveryErrors.length > 0 && (
              <Alert severity="warning">
                <AlertTitle>Some segments couldn't be discovered, so they aren't checked below</AlertTitle>
                <Box component="ul" m={0} pl={2}>
                  {selectedTargets.discoveryErrors.map((discoveryError) => (
                    <li key={discoveryError.path}>
                      <strong>{discoveryError.path}</strong>: {discoveryError.message}
                    </li>
                  ))}
                </Box>
              </Alert>
            )}

            {incompleteCountryGroupIds.length > 0 && (
              <Alert severity="warning">
                <AlertTitle>
                  Couldn't load every hero planner row for{' '}
                  {incompleteCountryGroupIds.map((id) => countryGroupNames.get(id) ?? id).join(', ')}
                </AlertTitle>
                Segments without tail deals can't fall back to their hero planner row, and every experience segment is
                checked even if its city isn't sent.
              </Alert>
            )}

            {targets.length === 0 && (
              <Alert severity="info">No Hero Planner segments were found for {selectedCountryGroupsName}.</Alert>
            )}

            {summary.loading === 0 && summary.regenerating > 0 && (
              <Alert severity="warning">
                <AlertTitle>
                  {summary.regenerating} segment{summary.regenerating === 1 ? ' is' : 's are'} still regenerating tail
                  deals
                </AlertTitle>
                They can't be sent until generation finishes. Generation normally takes a few minutes; if they stay like
                this the job has likely died, so regenerate the country group from the Hero Planner page.
              </Alert>
            )}

            {summary.loading === 0 && summary.heroOnly > 0 && (
              <Alert severity="info">
                <AlertTitle>
                  {summary.heroOnly} segment{summary.heroOnly === 1 ? " doesn't" : "s don't"} have tail deals yet
                </AlertTitle>
                They're shown with the hero planner row their schedule will be built from. They can't be sent, and the
                pre-send check fails, until tail deals are generated for {sendDate.format('D MMMM')}.
              </Alert>
            )}

            {isEverythingPassing && (
              <Alert severity="success">
                Every segment in {selectedCountryGroupsName} that gets sent has a schedule for{' '}
                {sendDate.format('dddd D MMMM YYYY')}, and {isAllCountryGroups ? 'every' : 'its'} pre-send check passes.
              </Alert>
            )}

            <Stack gap={2}>
              {sections.map((section) => (
                <HeroPlannerDebugCountryGroupSection
                  key={section.countryGroup.id}
                  brand={brand}
                  countryGroupName={section.countryGroup.name}
                  sendDate={sendDateValue}
                  summary={section.summary}
                  visibleRows={section.visibleRows}
                  preSendChecks={section.preSendChecks}
                  offerId={deferredOfferId}
                  onViewResponse={setInspectedRow}
                  onCopyUrl={handleCopyUrl}
                  onRetry={handleRetry}
                />
              ))}
            </Stack>
          </>
        )}
      </Stack>

      <HeroPlannerDebugResponseDialog row={inspectedRow} onClose={closeInspectedRow} />
    </Container>
  );
}

export default HeroPlannerDebugPage;
